/**
 * HexLayout - Shared layout component for hex-related pages.
 * Provides consistent header, collapsible sidebar, and content area
 * for pages like Hex View, Building Detail, and Character Detail.
 *
 * Includes built-in Quick Links navigation, Resource Collection, and
 * Your Resources sections when appropriate props are provided.
 */

import { Head, Link } from '@inertiajs/react';
import { ReactNode } from 'react';
import { HeaderNavButtons } from '@/components/FloatingTomeIcon';
import { useSidebarState } from '@/components/CollapsibleSidebar';
import { HexQuickLinks } from '@/components/HexQuickLinks/HexQuickLinks';
import { HexResources, type ResourceData } from '@/components/HexResources';
import { LeafCanopyHeader } from '@/components/LeafCanopyHeader';

/**
 * Minimal hex data needed for the layout header.
 */
interface HexLayoutHex {
    id: number;
    name: string;
}

/**
 * Context passed to sidebar render function.
 */
interface SidebarContext {
    /** Close the sidebar (useful for mobile) */
    closeSidebar: () => void;
    /** Whether the current viewport is mobile */
    isMobile: boolean;
}

/**
 * Back link configuration.
 */
interface BackLinkConfig {
    /** The URL to link to */
    href: string;
    /** The label to display */
    label: string;
}

/**
 * Props for resource collection section.
 */
interface ResourceCollectionConfig {
    /** Whether resources can be collected */
    canCollect: boolean;
    /** Whether currently collecting */
    isCollecting: boolean;
    /** Handler for collecting resources */
    onCollect: () => void;
    /** Minutes until next collection (when not canCollect) */
    minutesUntilCollection?: number | null;
}

/**
 * Props for Quick Links configuration.
 */
interface QuickLinksConfig {
    /** The hex ID for building Quick Links URLs */
    hexId: number;
    /** Whether the current user is the hex owner (for filtering links) */
    isOwner: boolean;
    /** Whether this is an open hex */
    isOpenHex?: boolean;
    /** Whether this is an enclave hex */
    isEnclave?: boolean;
    /** Currently active item ID for highlighting */
    activeItem?: string;
    /** Tab change handler for in-page navigation */
    onTabChange?: (tab: string) => void;
    /** Vault item count for badge */
    vaultCount?: number;
    /** In-progress attempts count for badge */
    inProgressCount?: number;
}

/**
 * Props for the HexLayout component.
 */
interface HexLayoutProps {
    /** Page title for the browser tab */
    title: string;
    /** Hex data for header display and navigation */
    hex: HexLayoutHex;
    /** Whether the current user is the hex owner */
    isOwner: boolean;
    /** Sidebar content - can be a ReactNode or a render function that receives sidebar context */
    sidebar: ReactNode | ((context: SidebarContext) => ReactNode);
    /** Main content to render */
    children: ReactNode;
    /** Custom back link (defaults to "Back to Map" -> /map) */
    backLink?: BackLinkConfig;
    /** Quick Links configuration (shown when provided) */
    quickLinks?: QuickLinksConfig;
    /** User's resources (shown when provided, owner only) */
    userResources?: ResourceData | null;
    /** Resource collection configuration (shown when provided, owner only) */
    resourceCollection?: ResourceCollectionConfig;
}

/**
 * HexLayout - Layout component for hex-related pages.
 * Provides a consistent header with navigation, collapsible sidebar, and content area.
 * Sidebar is hidden by default on mobile and visible on desktop.
 */
export default function HexLayout({
    title,
    hex,
    isOwner,
    sidebar,
    children,
    backLink,
    quickLinks,
    userResources,
    resourceCollection,
}: HexLayoutProps) {
    // Use responsive sidebar state - hidden by default on mobile, visible on desktop
    const { isOpen: sidebarOpen, isMobile, toggle: toggleSidebar, close: closeSidebar } = useSidebarState(true, false);

    // Default back link to Map
    const defaultBackLink: BackLinkConfig = { href: '/map', label: 'Back to Map' }
    const actualBackLink = backLink || defaultBackLink;

    return (
        <>
            <Head title={title} />

            <div className="min-h-screen bg-slate-900 flex flex-col">
                <LeafCanopyHeader className="flex-shrink-0">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2 md:gap-4">
                            {/* Mobile sidebar toggle in header */}
                            <button
                                onClick={toggleSidebar}
                                className="md:hidden p-2 text-emerald-100 hover:text-white hover:bg-green-800/50 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-emerald-400"
                                aria-label={sidebarOpen ? 'Close sidebar' : 'Open sidebar'}
                                aria-expanded={sidebarOpen}
                            >
                                {/* Hamburger / X icon */}
                                <span className="block w-5 h-5 relative">
                                    <span
                                        className={`
                                            absolute left-0 w-5 h-0.5 bg-current
                                            transition-all duration-300
                                            ${sidebarOpen ? 'top-2 rotate-45' : 'top-1'}
                                        `}
                                    />
                                    <span
                                        className={`
                                            absolute left-0 top-2 w-5 h-0.5 bg-current
                                            transition-all duration-300
                                            ${sidebarOpen ? 'opacity-0' : 'opacity-100'}
                                        `}
                                    />
                                    <span
                                        className={`
                                            absolute left-0 w-5 h-0.5 bg-current
                                            transition-all duration-300
                                            ${sidebarOpen ? 'top-2 -rotate-45' : 'top-3'}
                                        `}
                                    />
                                </span>
                            </button>
                            <Link
                                href={actualBackLink.href}
                                className="text-green-950 font-bold hover:text-white transition-colors cursor-pointer [text-shadow:_0_0_8px_rgba(255,255,255,0.8),_0_0_2px_rgba(255,255,255,1)]"
                            >
                                ← <span className="hidden sm:inline">{actualBackLink.label}</span><span className="sm:hidden">{actualBackLink.label.replace('Back to ', '')}</span>
                            </Link>
                            <h1 className="text-lg md:text-xl font-bold text-green-950 [text-shadow:_0_0_8px_rgba(255,255,255,0.8),_0_0_2px_rgba(255,255,255,1)] truncate max-w-[120px] sm:max-w-none">{hex.name}</h1>
                        </div>

                        {/* Desktop navigation buttons - right side of header */}
                        <HeaderNavButtons userHex={isOwner ? hex : null} />
                    </div>
                </LeafCanopyHeader>

                {/* Main content with sidebar */}
                <div className="flex-1 flex overflow-hidden relative">
                    {/* Mobile overlay backdrop - shown when sidebar is open on mobile */}
                    {isMobile && sidebarOpen && (
                        <div
                            className="fixed inset-0 bg-black/50 z-40 md:hidden"
                            onClick={closeSidebar}
                            aria-hidden="true"
                        />
                    )}

                    {/* Left sidebar - collapsible with wooden texture */}
                    <aside
                        className={`
                            ${sidebarOpen ? 'w-64' : 'w-0'}
                            flex-shrink-0 overflow-hidden
                            transition-all duration-300 ease-in-out
                            ${isMobile ? 'fixed inset-y-0 left-0 z-50 top-[57px]' : 'relative'}
                        `}
                        style={{
                            background: 'linear-gradient(180deg, #4a3728 0%, #3d2e22 50%, #2f231a 100%)',
                            borderRight: '3px solid #5c4a3d',
                        }}
                    >
                        {/* Wood grain texture overlay */}
                        <div
                            className="absolute inset-0 pointer-events-none opacity-30"
                            style={{
                                backgroundImage: `
                                    repeating-linear-gradient(
                                        0deg,
                                        transparent 0px,
                                        transparent 8px,
                                        rgba(0,0,0,0.1) 8px,
                                        rgba(0,0,0,0.1) 9px
                                    ),
                                    repeating-linear-gradient(
                                        90deg,
                                        transparent 0px,
                                        transparent 2px,
                                        rgba(60,40,20,0.1) 2px,
                                        rgba(60,40,20,0.1) 4px
                                    )
                                `,
                            }}
                        />
                        {/* Wood edge highlight */}
                        <div
                            className="absolute top-0 left-0 bottom-0 w-1 pointer-events-none"
                            style={{
                                background: 'linear-gradient(180deg, rgba(139,115,85,0.3) 0%, rgba(139,115,85,0.1) 100%)',
                            }}
                        />
                        <div className="relative w-64 h-full overflow-y-auto">
                            {/* Quick Links - shown when configured */}
                            {quickLinks && (
                                <HexQuickLinks
                                    hexId={quickLinks.hexId}
                                    isOwner={quickLinks.isOwner}
                                    isOpenHex={quickLinks.isOpenHex}
                                    isEnclave={quickLinks.isEnclave}
                                    activeItem={quickLinks.activeItem}
                                    collapsible
                                    onTabChange={quickLinks.onTabChange}
                                    closeSidebar={closeSidebar}
                                    isMobile={isMobile}
                                    vaultCount={quickLinks.vaultCount}
                                    inProgressCount={quickLinks.inProgressCount}
                                />
                            )}

                            {/* Custom sidebar content */}
                            {typeof sidebar === 'function'
                                ? sidebar({ closeSidebar, isMobile })
                                : sidebar}

                            {/* Resource Collection Section (owner only) - themed panel */}
                            {isOwner && resourceCollection && (
                                <div className="mx-3 my-4">
                                    <div
                                        className="relative rounded-lg overflow-hidden"
                                        style={{
                                            background: 'linear-gradient(145deg, #5D4E37 0%, #4A3F30 50%, #3D3225 100%)',
                                            boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), 0 2px 4px rgba(0,0,0,0.3)',
                                            border: '2px solid #6B5B47',
                                        }}
                                    >
                                        {/* Panel header */}
                                        <div
                                            className="px-3 py-2"
                                            style={{
                                                background: 'linear-gradient(180deg, rgba(139,115,85,0.3) 0%, rgba(139,115,85,0.1) 100%)',
                                                borderBottom: '1px solid rgba(139,115,85,0.3)',
                                            }}
                                        >
                                            <h2
                                                className="text-xs font-bold uppercase tracking-wider"
                                                style={{ color: '#D4A574' }}
                                            >
                                                Resource Collection
                                            </h2>
                                        </div>

                                        <div className="p-3">
                                            {resourceCollection.canCollect && (
                                                <button
                                                    onClick={resourceCollection.onCollect}
                                                    disabled={resourceCollection.isCollecting}
                                                    className="w-full px-3 py-2 text-sm font-bold rounded transition-all cursor-pointer disabled:cursor-not-allowed"
                                                    style={{
                                                        background: resourceCollection.isCollecting
                                                            ? 'linear-gradient(180deg, #3D5A3D 0%, #2D4A2D 100%)'
                                                            : 'linear-gradient(180deg, #4D7A4D 0%, #3D6A3D 50%, #2D5A2D 100%)',
                                                        border: '2px solid #5D8A5D',
                                                        color: '#E8F5E8',
                                                        boxShadow: resourceCollection.isCollecting
                                                            ? 'none'
                                                            : '0 2px 4px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.2)',
                                                    }}
                                                >
                                                    {resourceCollection.isCollecting ? 'Collecting...' : 'Collect Resources'}
                                                </button>
                                            )}

                                            {!resourceCollection.canCollect && (
                                                <div className="text-center">
                                                    <p className="text-xs mb-1" style={{ color: '#A67C52' }}>
                                                        Next collection in:
                                                    </p>
                                                    <p
                                                        className="text-lg font-bold"
                                                        style={{ color: '#F5DEB3' }}
                                                    >
                                                        {resourceCollection.minutesUntilCollection !== null
                                                            ? `${resourceCollection.minutesUntilCollection} min${resourceCollection.minutesUntilCollection !== 1 ? 's' : ''}`
                                                            : 'Calculating...'}
                                                    </p>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* User's Total Resources (owner only) - Pip-Boy style */}
                            {isOwner && userResources && (
                                <div className="mx-3 my-4">
                                    <HexResources resources={userResources} compact showHeaders />
                                </div>
                            )}
                        </div>
                    </aside>

                    {/* Desktop sidebar toggle switch - horizontal, to right of sidebar */}
                    <div
                        className="hidden md:block absolute z-30 transition-all duration-300"
                        style={{
                            left: sidebarOpen ? 'calc(16rem + 0.5rem)' : '0.5rem',
                            top: '-4px',
                        }}
                    >
                        {/* Panel that comes from behind header */}
                        <div
                            className="relative pt-3 pb-2 px-3 rounded-b-lg"
                            style={{
                                background: 'linear-gradient(180deg, #4A4540 0%, #3A3530 50%, #2A2520 100%)',
                                border: '2px solid #5A5550',
                                borderTop: 'none',
                                boxShadow: '0 3px 6px rgba(0,0,0,0.3), inset 0 -1px 0 rgba(255,255,255,0.05)',
                            }}
                        >
                            {/* Panel texture */}
                            <div
                                className="absolute inset-0 rounded-b-lg opacity-20 pointer-events-none"
                                style={{
                                    backgroundImage: 'repeating-linear-gradient(90deg, transparent 0px, transparent 2px, rgba(0,0,0,0.1) 2px, rgba(0,0,0,0.1) 4px)',
                                }}
                            />
                            {/* Small rivets on panel */}
                            <div
                                className="absolute bottom-1.5 left-1.5 w-1 h-1 rounded-full"
                                style={{ background: 'radial-gradient(circle at 30% 30%, #6A6560, #3A3530)' }}
                            />
                            <div
                                className="absolute bottom-1.5 right-1.5 w-1 h-1 rounded-full"
                                style={{ background: 'radial-gradient(circle at 30% 30%, #6A6560, #3A3530)' }}
                            />

                            {/* Switch button */}
                            <button
                                onClick={toggleSidebar}
                                className="relative cursor-pointer focus:outline-none focus:ring-2 focus:ring-amber-400 rounded"
                                aria-label={sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}
                                aria-expanded={sidebarOpen}
                            >
                                {/* Switch housing - horizontal */}
                                <div
                                    className="relative h-3 w-10 rounded-sm"
                                    style={{
                                        background: 'linear-gradient(180deg, #2A2520 0%, #1A1510 100%)',
                                        border: '1px solid #1A1510',
                                        boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.6)',
                                    }}
                                >
                                    {/* Switch track */}
                                    <div
                                        className="absolute top-1/2 -translate-y-1/2 left-1 right-1 h-1.5 rounded-sm"
                                        style={{
                                            background: 'linear-gradient(180deg, #0A0A08 0%, #1A1510 100%)',
                                            boxShadow: 'inset 0 1px 1px rgba(0,0,0,0.5)',
                                        }}
                                    />
                                    {/* Switch lever */}
                                    <div
                                        className="absolute top-1/2 -translate-y-1/2 h-2.5 w-4 rounded-sm transition-all duration-200"
                                        style={{
                                            left: sidebarOpen ? 'calc(100% - 18px)' : '2px',
                                            background: sidebarOpen
                                                ? 'linear-gradient(180deg, #CD7F32 0%, #B8860B 50%, #8B6914 100%)'
                                                : 'linear-gradient(180deg, #6B6560 0%, #5A5550 50%, #4A4540 100%)',
                                            border: sidebarOpen ? '1px solid #DAA520' : '1px solid #6A6560',
                                            boxShadow: sidebarOpen
                                                ? '0 1px 2px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.3)'
                                                : '0 1px 2px rgba(0,0,0,0.3), inset 0 1px 0 rgba(255,255,255,0.1)',
                                        }}
                                    >
                                        {/* Grip line on lever */}
                                        <div
                                            className="absolute left-1/2 -translate-x-1/2 top-1 bottom-1 w-px"
                                            style={{ background: 'rgba(0,0,0,0.3)' }}
                                        />
                                    </div>
                                </div>
                            </button>
                        </div>
                    </div>

                    {/* Right Content Area */}
                    <main className="flex-1 overflow-y-auto">
                        {children}
                    </main>
                </div>
            </div>
        </>
    );
}

export type { HexLayoutHex, HexLayoutProps, SidebarContext, BackLinkConfig, QuickLinksConfig, ResourceCollectionConfig };
